import { useIntl } from '@/intl'
import Layout from '@/scopes/prices/components/Layout'
import { Banner } from '@sunwebgroup/uds-ui/banner'
import { Button } from '@sunwebgroup/uds-ui/button'
import { TextField } from '@sunwebgroup/uds-ui/fields/text-field'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import styles from './AlertService.module.css'
import { AlertServiceErrorBanner } from './AlertServiceErrorBanner'
import { useAlertService } from './hooks'

export function AlertService({ contextItemId, alertServiceDataSourceId, accommodationId }: { contextItemId: string, alertServiceDataSourceId?: string, accommodationId?: number }): React.ReactElement {
  const intl = useIntl()
  const [email, setEmail] = React.useState('')
  const [isValidEmail, setIsValidEmail] = React.useState(true)
  const { mutate, isLoading, isSuccess, isError, reset: resetMutationState } = useAlertService(contextItemId, alertServiceDataSourceId, accommodationId)

  const checkEmailValidity = (value: string) => {
    const emailRegex = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/
    return emailRegex.test(value)
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value)
  }

  const handleOnBlur = () => {
    const isValid = checkEmailValidity(email)
    setIsValidEmail(isValid)
  }

  const handleConfirm = () => {
    const isValid = checkEmailValidity(email)
    if (isValid) {
      resetMutationState()
      mutate(email)
    }
    else {
      setIsValidEmail(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleConfirm()
    }
  }

  return (
    <Layout.Root>
      <Icon glyph="Envelope" size={32} className={styles.envelopeIcon} />
      <Layout.Content>
        <div className={styles.contentWrapper}>
          <Layout.TitleDescription>
            <Typography variant="body-l-bold-snug">{intl.formatMessage({ id: 'price_alert_service-title' })}</Typography>
            <Typography variant="body-m-regular-snug">{intl.formatMessage({ id: 'price_alert_service-description' })}</Typography>
          </Layout.TitleDescription>
          {isSuccess && (
            <Banner color="success" size="M" text={intl.formatMessage({ id: 'price_alert_service-title' })} supportingText={intl.formatMessage({ id: 'price_alert_service-success-description' })} />
          )}
          {!isSuccess && (
            <div className={styles.formWrapper}>
              <TextField
                label={intl.formatMessage({ id: 'email_address' })}
                value={email}
                onChange={handleOnChange}
                onBlur={handleOnBlur}
                feedbackText={intl.formatMessage({ id: 'price_alert_service-error' })}
                status={!isValidEmail ? 'error' : 'default'}
                onKeyDown={handleKeyDown}
              />
              <Button onClick={handleConfirm} disabled={isLoading} fullWidth={false} className={styles.submitButton}>
                {intl.formatMessage({ id: 'prices_no_available_button' })}
              </Button>
              <Typography variant="body-s-regular-tight" className={styles.privacyMessage}>
                {intl.formatMessage({ id: 'price_alert_service-privacy-message' })}
              </Typography>
            </div>
          )}
          {isError && <AlertServiceErrorBanner />}
        </div>
      </Layout.Content>
    </Layout.Root>
  )
}
